import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../core/contexts/AuthContext';
import { usePermissions } from '../core/hooks/usePermissions';
import { getDefaultRedirectPath } from '../core/utils/authHelpers';
import Alert from '../shared/components/ui/Alert';

function checkPassword(pw) {
  const errors = [];
  if (pw.length < 8)            errors.push('Mindst 8 tegn');
  if (!/[A-Z]/.test(pw))        errors.push('Stort bogstav');
  if (!/[a-z]/.test(pw))        errors.push('Lille bogstav');
  if (!/[0-9]/.test(pw))        errors.push('Et tal');
  if (!/[^A-Za-z0-9]/.test(pw)) errors.push('Specialtegn');
  return errors;
}

export default function ChangePasswordPage() {
  const { t } = useTranslation('auth');
  const navigate = useNavigate();
  const { setMustChangePassword, userRoles } = useAuth();

  const [password, setPassword] = useState('');
  const [confirm, setConfirm]   = useState('');
  const [pwErrors, setPwErrors] = useState([]);
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState('');

  const handlePasswordChange = (val) => {
    setPassword(val);
    setPwErrors(val ? checkPassword(val) : []);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    const errs = checkPassword(password);
    if (errs.length > 0) { setPwErrors(errs); return; }
    if (password !== confirm) { setError(t('changePassword.error.passwordMismatch')); return; }

    setLoading(true);
    try {
      const res = await fetch('/api/player-auth-ext/change-password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ newPassword: password }),
      });
      const data = await res.json();

      if (!res.ok) {
        setError(data.error || t('changePassword.error.changeFailed'));
        return;
      }

      setMustChangePassword(false);
      navigate(getDefaultRedirectPath(userRoles), { replace: true });
    } catch {
      setError(t('changePassword.error.changeFailed'));
    } finally {
      setLoading(false);
    }
  };

  const inputClass = 'mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500';

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <h1 className="text-3xl font-bold text-gray-900 text-center mb-2">
          {t('changePassword.heading.title')}
        </h1>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-white py-8 px-4 shadow-xl rounded-lg sm:px-10">

          <Alert variant="warning" className="mb-6 text-sm">
            {t('changePassword.text.required')}
          </Alert>

          {error && <Alert variant="error" className="mb-4 text-sm">{error}</Alert>}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                {t('changePassword.input.label.password')}
              </label>
              <input
                id="password"
                type="password"
                value={password}
                onChange={(e) => handlePasswordChange(e.target.value)}
                className={inputClass}
                placeholder={t('changePassword.input.placeholder.password')}
                required
                disabled={loading}
                autoFocus
              />
              {password && (
                <ul className="mt-1 space-y-0.5">
                  {['Mindst 8 tegn', 'Stort bogstav', 'Lille bogstav', 'Et tal', 'Specialtegn'].map((req) => (
                    <li key={req} className={`text-xs flex items-center gap-1 ${pwErrors.includes(req) ? 'text-red-500' : 'text-green-600'}`}>
                      {pwErrors.includes(req) ? '✗' : '✓'} {req}
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <div>
              <label htmlFor="confirm" className="block text-sm font-medium text-gray-700">
                {t('changePassword.input.label.confirmPassword')}
              </label>
              <input
                id="confirm"
                type="password"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                className={`${inputClass} ${confirm && confirm !== password ? 'border-red-300' : ''}`}
                placeholder={t('changePassword.input.placeholder.confirmPassword')}
                required
                disabled={loading}
              />
            </div>

            <button
              type="submit"
              disabled={loading || pwErrors.length > 0}
              className="w-full flex justify-center py-2.5 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {loading ? t('changePassword.button.changing') : t('changePassword.button.change')}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
